import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Clock, Send, CheckCircle2, AlertTriangle,
  RefreshCw, Sparkles, Save, Download, Calendar, Paperclip,
  ChevronRight, AlertCircle, User, Building2, DollarSign, Loader2
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { formatCurrency, formatDate } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

async function getAppeal(appealId) {
  const response = await fetch(`${API_URL}/api/appeals/${appealId}`);
  if (!response.ok) throw new Error('Failed to fetch appeal');
  return response.json();
}

async function generateLetter(appealId) {
  const response = await fetch(`${API_URL}/api/appeals/${appealId}/generate-letter`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Failed to generate letter');
  return response.json();
}

async function updateAppeal(appealId, updates) {
  const response = await fetch(`${API_URL}/api/appeals/${appealId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });
  if (!response.ok) throw new Error('Failed to update appeal');
  return response.json();
}

async function submitAppeal(appealId) {
  const response = await fetch(`${API_URL}/api/appeals/${appealId}/submit`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Failed to submit appeal');
  return response.json();
}

function getUrgencyConfig(urgency) {
  const configs = {
    'OVERDUE': { color: 'bg-red-600 text-white', text: 'Overdue' },
    'CRITICAL': { color: 'bg-red-500 text-white', text: 'Critical' },
    'HIGH': { color: 'bg-orange-500 text-white', text: 'High' },
    'MEDIUM': { color: 'bg-yellow-500 text-black', text: 'Medium' },
    'NORMAL': { color: 'bg-blue-500 text-white', text: 'Normal' },
    'LOW': { color: 'bg-green-500 text-white', text: 'Low' }
  };
  return configs[urgency] || configs['NORMAL'];
}

function getStatusConfig(status) {
  const configs = {
    'DRAFT': { color: 'bg-gray-100 text-gray-800', icon: FileText },
    'PENDING_REVIEW': { color: 'bg-blue-100 text-blue-800', icon: Clock },
    'APPROVED': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    'SUBMITTED': { color: 'bg-purple-100 text-purple-800', icon: Send },
    'ACCEPTED': { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
    'REJECTED': { color: 'bg-red-100 text-red-800', icon: AlertTriangle }
  };
  return configs[status] || configs['DRAFT'];
}

function AppealTimeline({ timeline }) {
  if (!timeline || timeline.length === 0) return null;

  return (
    <div className="space-y-4">
      {timeline.map((event, idx) => (
        <div key={idx} className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-primary" />
          <div className="flex-1">
            <p className="text-sm font-medium">{event.action.replace(/_/g, ' ')}</p>
            <p className="text-xs text-muted-foreground">{event.description}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatDate(event.timestamp)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function RequiredDocuments({ documents, attachments }) {
  return (
    <div className="space-y-2">
      {documents.map((doc, idx) => {
        const isAttached = attachments?.includes(doc);
        return (
          <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
            <div className="flex items-center space-x-2">
              <Paperclip className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">{doc}</span>
            </div>
            {isAttached ? (
              <CheckCircle2 className="w-4 h-4 text-green-500" />
            ) : (
              <div className="w-4 h-4 rounded-full border-2 border-muted-foreground" />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function AppealDetailPage() {
  const { appealId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editedLetter, setEditedLetter] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const { data: appeal, isLoading, error } = useQuery({
    queryKey: ['appeal', appealId],
    queryFn: () => getAppeal(appealId),
  });

  useEffect(() => {
    if (appeal?.generated_letter) {
      setEditedLetter(appeal.generated_letter);
    }
  }, [appeal?.generated_letter]);

  const generateMutation = useMutation({
    mutationFn: () => generateLetter(appealId),
    onSuccess: (data) => {
      toast.success('Appeal letter generated successfully!');
      setEditedLetter(data.letter);
      queryClient.invalidateQueries(['appeal', appealId]);
    },
    onError: (error) => {
      toast.error(`Failed to generate letter: ${error.message}`);
    }
  });

  const saveMutation = useMutation({
    mutationFn: (updates) => updateAppeal(appealId, updates),
    onSuccess: () => {
      toast.success('Appeal saved successfully!');
      setHasChanges(false);
      queryClient.invalidateQueries(['appeal', appealId]);
    },
    onError: (error) => {
      toast.error(`Failed to save: ${error.message}`);
    }
  });

  const submitMutation = useMutation({
    mutationFn: () => submitAppeal(appealId),
    onSuccess: () => {
      toast.success('Appeal submitted successfully!');
      queryClient.invalidateQueries(['appeal', appealId]);
    },
    onError: (error) => {
      toast.error(`Failed to submit: ${error.message}`);
    }
  });

  const handleLetterChange = (value) => {
    setEditedLetter(value);
    setHasChanges(value !== appeal?.generated_letter);
  };

  const handleSave = () => {
    saveMutation.mutate({ generated_letter: editedLetter });
  };

  const handleStatusChange = (newStatus) => {
    saveMutation.mutate({ status: newStatus });
  };

  if (isLoading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Error loading appeal: {error.message}</p>
            <Button className="mt-4" onClick={() => navigate('/appeals')}>Back to Appeals</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const urgencyConfig = getUrgencyConfig(appeal.urgency);
  const statusConfig = getStatusConfig(appeal.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="appeal-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/appeals')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Appeal Details</h1>
            <p className="text-sm text-muted-foreground">Claim: {appeal.claim_id}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Badge className={statusConfig.color}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {appeal.status?.replace('_', ' ')}
          </Badge>
          <Badge className={urgencyConfig.color}>
            {urgencyConfig.text}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Appeal Info Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="text-xs flex items-center">
                  <DollarSign className="w-3 h-3 mr-1" />Amount
                </CardDescription>
                <CardTitle className="text-xl">{formatCurrency(appeal.denial_amount)}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="text-xs flex items-center">
                  <Calendar className="w-3 h-3 mr-1" />Deadline
                </CardDescription>
                <CardTitle className="text-lg">
                  {appeal.days_remaining >= 0 ? `${appeal.days_remaining} days` : 'Overdue'}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="text-xs flex items-center">
                  <Building2 className="w-3 h-3 mr-1" />Payer
                </CardDescription>
                <CardTitle className="text-sm truncate">{appeal.payer_name}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="text-xs flex items-center">
                  <User className="w-3 h-3 mr-1" />Patient
                </CardDescription>
                <CardTitle className="text-sm truncate">{appeal.patient_name || 'N/A'}</CardTitle>
              </CardHeader>
            </Card>
          </div>

          {/* Letter Editor */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Appeal Letter</CardTitle>
                  <CardDescription>
                    {appeal.letter_confidence > 0 && (
                      <span className="flex items-center mt-1">
                        <Sparkles className="w-3 h-3 mr-1" />
                        AI Confidence: {(appeal.letter_confidence * 100).toFixed(0)}%
                      </span>
                    )}
                  </CardDescription>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => generateMutation.mutate()}
                    disabled={generateMutation.isPending}
                  >
                    {generateMutation.isPending ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    {editedLetter ? 'Regenerate' : 'Generate'}
                  </Button>
                  {hasChanges && (
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={saveMutation.isPending}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {editedLetter ? (
                <Textarea
                  value={editedLetter}
                  onChange={(e) => handleLetterChange(e.target.value)}
                  className="min-h-[400px] font-mono text-sm"
                  placeholder="Appeal letter content..."
                />
              ) : (
                <div className="min-h-[400px] flex items-center justify-center border-2 border-dashed rounded-lg">
                  <div className="text-center">
                    <Sparkles className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">No letter generated yet</p>
                    <Button onClick={() => generateMutation.mutate()} disabled={generateMutation.isPending}>
                      {generateMutation.isPending ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      Generate with AI
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Appeal Strategy */}
          {appeal.appeal_strategy && (
            <Card className="border-primary/50 bg-primary/5">
              <CardHeader>
                <CardTitle className="text-sm flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Recommended Appeal Strategy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{appeal.appeal_strategy}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {appeal.status === 'DRAFT' && (
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => handleStatusChange('PENDING_REVIEW')}
                >
                  <ChevronRight className="w-4 h-4 mr-2" />
                  Submit for Review
                </Button>
              )}
              {appeal.status === 'PENDING_REVIEW' && (
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => handleStatusChange('APPROVED')}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              )}
              {appeal.status === 'APPROVED' && (
                <Button
                  className="w-full"
                  onClick={() => submitMutation.mutate()}
                  disabled={submitMutation.isPending}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit to Payer
                </Button>
              )}
              <Button 
                className="w-full" 
                variant="outline"
                onClick={async () => {
                  if (!editedLetter && !appeal.generated_letter) {
                    toast.error('Please generate an appeal letter first');
                    return;
                  }
                  
                  setDownloadingPdf(true);
                  try {
                    // Fetch PDF as blob to properly handle filename
                    const pdfUrl = `${API_URL}/api/appeals/${appealId}/download-pdf`;
                    const response = await fetch(pdfUrl);
                    
                    if (!response.ok) {
                      throw new Error('Failed to download PDF');
                    }
                    
                    // Get filename from Content-Disposition header or use default
                    const contentDisposition = response.headers.get('Content-Disposition');
                    let filename = `appeal_${appeal.claim_id || appealId}.pdf`;
                    if (contentDisposition) {
                      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                      if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1].replace(/['"]/g, '');
                      }
                    }
                    
                    // Create blob and download
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    toast.success('PDF downloaded successfully');
                  } catch (error) {
                    console.error('PDF download error:', error);
                    toast.error('Failed to download PDF');
                  } finally {
                    setDownloadingPdf(false);
                  }
                }}
                disabled={downloadingPdf}
                data-testid="download-pdf-btn"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download PDF
              </Button>
            </CardContent>
          </Card>

          {/* Denial Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Denial Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground">Category</p>
                <p className="text-sm font-medium">{appeal.denial_category}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Denial Codes</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {appeal.denial_codes?.map((code, idx) => (
                    <Badge key={idx} variant="outline">{code}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Service Date</p>
                <p className="text-sm">{appeal.service_date || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Appeal Type</p>
                <p className="text-sm">{appeal.appeal_type?.replace('_', ' ')}</p>
              </div>
            </CardContent>
          </Card>

          {/* Required Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Required Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <RequiredDocuments
                documents={appeal.required_documents || []}
                attachments={appeal.attachments || []}
              />
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <AppealTimeline timeline={appeal.timeline} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
